import { call, put, select, takeLatest } from 'redux-saga/effects'
import { v4 as uuidv4 } from 'uuid'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { getServiceIdSelector, getUserIdSelector } from '../../user/selectors'
import { getPcrIdSelector } from '../../home/selectors'
import { IAttachment, IPcr } from '../../pcr/store/pcr.types'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { FillablePDFsConstants } from '../store/fillablePDFs.constants'
import { IFillablePDFsState } from '../store/fillablePDFs.types'
import { getFillablePDFsSelector } from '../selectors'
import { getCameraAttachmentsOptionsSelector, getFillablePDFOptionsSelector } from '../../options/selectors'
import { FillablePdfsService } from '../services/fillablePdfs.service'
import { fillPdfWithCustomInputs } from '../../../utilities/pdfFill.util'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { resetFillablePDFsState, setFillablePDFsList } from '../store/fillablePDFs.actions'
import Logger from '../../../services/logger.service'
import { getDateTime } from '../../../utilities'
import { ICameraAttachmentsOptionsState, IFillablePDFOptionsState } from '../../options/store/options.types'
import { IClientDataDoc } from '../../../services/clientData.service'

const uint8ToBase64 = (bytes: Uint8Array): string => {
  let binary = ''
  const chunkSize = 0x8000

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode(...chunk)
  }

  return btoa(binary)
}

export function* saveFillablePDF() {
  const pcrId: string = yield select(getPcrIdSelector)

  if (!pcrId) {
    return
  }

  try {
    const fillablePDFsState: IFillablePDFsState = yield select(getFillablePDFsSelector)
    const fillablePDFOptionsState: IFillablePDFOptionsState = yield select(getFillablePDFOptionsSelector)
    const serviceId: string = yield select(getServiceIdSelector)
    const userId: string = yield select(getUserIdSelector)
    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrId)
    const { fillablePDFsList, __currentStep, __deleteFillablePDFId, ...currentFillablePDF } = fillablePDFsState
    const cameraAttachmentsOptionsState: ICameraAttachmentsOptionsState = yield select(
      getCameraAttachmentsOptionsSelector
    )
    const { categoryOptions } = cameraAttachmentsOptionsState

    if (!currentFillablePDF.id) {
      return
    }

    const metadata = fillablePDFOptionsState?.metadata ?? []
    const row = metadata.find((f: any) => f.FormNameWithId === currentFillablePDF.id)

    if (!row?.FormNameWithId) {
      return
    }

    const localService = new FillablePdfsService(serviceId)
    const pdfBlobDoc: IClientDataDoc = yield call(
      [localService, localService.getFillablePdfBlobDoc],
      row.FormNameWithId
    )

    if (!pdfBlobDoc?.entity || typeof pdfBlobDoc.entity !== 'string') {
      return
    }

    Logger.info(
      `Attempting to save fillable PDF ${row.FormNameWithId} to PCR: ${pcrId} saved by user: ${userId} in service: ${serviceId}`
    )

    const filledBuffer: ArrayBuffer = yield call(
      fillPdfWithCustomInputs,
      pdfBlobDoc.entity,
      currentFillablePDF.customInputs ?? []
    )

    const filledBytes = new Uint8Array(filledBuffer)
    const pdfBase64 = uint8ToBase64(filledBytes)
    const attachmentName = `${row.FormNameWithId}.pdf`
    const category = categoryOptions.find((o) => o.id === row.CategoryId) ?? null

    const attachment: IAttachment = {
      fileExtension: 'pdf',
      data: pdfBase64,
      category: category,
      name: attachmentName,
      uploadedDt: getDateTime(),
      id: uuidv4(),
    }

    const updatedPdfAttachments = [...(pcrDoc.pdfAttachments || []), attachment]

    const pcrDocUpdated: IPcr = {
      ...pcrDoc,
      pdfAttachments: updatedPdfAttachments,
    }

    yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)

    Logger.info(
      `Successfully saved fillable PDF ${attachment.name} to PCR: ${pcrId} saved by user: ${userId} in service: ${serviceId}`
    )

    yield put(setFillablePDFsList(updatedPdfAttachments))
    yield put(resetFillablePDFsState())
  } catch (e) {
    yield put(throwPcrException(`saveFillablePDF() error: ${e}`))
  }
}

export function* saveFillablePDFSaga() {
  yield takeLatest([FillablePDFsConstants.SAVE_FILLABLE_PDF], performWithMutexHOG, saveFillablePDF)
}
